import { XO_SESSION_TOKEN } from '@constants/membership';
import { loadableReady } from '@loadable/component';
import { NewrelicHelpers } from '@utils/logger/internal/newrelicHelpers';
import { Logger } from '@utils/logger/logger';
import { getMember } from '@xo-union/store-membership-redux';
import createTabAccessibility from '@xo-union/tab-accessibility';
// @ts-check
import { CookieStorage } from 'cookie-storage';
import 'intersection-observer';
import { Provider as JotaiProvider } from 'jotai';
import { createStore as createJotaiStore } from 'jotai/vanilla';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { BrowserRouter } from 'react-router-dom';
import 'requestidlecallback-polyfill';
import { getVisitorId } from '../utils/cookies';
import { GtmSnippet } from './components/gtm-snippet';
import AnalyticsProvider from './contexts/AnalyticsContext/AnalyticsContext';
import { useViewportSync } from './hooks/use-viewport-sync';
import { reduxStoreAtom } from './jotai/redux-atoms';
import { getJotaiInitialValues } from './jotai/store';
import {
	appliedFacetsForImpressionsAtom,
	selectedCategoryCodesAtom,
} from './pages/VendorsSearch/containers/FilterPills/atoms';
import { getPageType } from './redux/page';
import * as settingActions from './redux/settings/actions';
import { transmitImpressions } from './redux/vendorImpressions/thunks';
import { setVisitorId } from './redux/visitor';
import routes from './routes/routes';
import createStore from './store';
import ensureAsyncReady from './utils/ensureAsyncReady';
import { createHydrationErrorReporter } from './utils/hydrationErrorReporter';
import { reportWebVitals } from './utils/reportWebVitals';

const logError = (error, info) => {
	Logger.error({
		log_message: error.message,
		optionalParams: { info },
		errorToLog: error,
		newRelic: true,
	});
};

const serviceWorker = () => {
	if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
		navigator.serviceWorker.getRegistrations().then((registrations) => {
			for (const registration of registrations) {
				registration.unregister();
			}
		});
	}
};

if (typeof window !== 'undefined') {
	window.Logger = Logger;
	window.NewrelicHelpers = NewrelicHelpers;

	serviceWorker();

	ensureAsyncReady(routes)
		.then(() => {
			const store = createStore(window.__INITIAL_STATE__); // eslint-disable-line
			const { getState, dispatch } = store;
			const state = getState();
			const pageType = getPageType(state);
			const { membership, vendor } = state;
			const memberId = membership?.member?.id || null;

			const cookieStorage = new CookieStorage();
			const sessionToken = cookieStorage.getItem(XO_SESSION_TOKEN);
			if (sessionToken) {
				dispatch(getMember()); // union hook to initialize membership in redux
			}

			// load visitor ID from cookie
			const visitorId = getVisitorId();
			if (visitorId) {
				dispatch(setVisitorId(visitorId));
			}

			const tabAccessbility = createTabAccessibility();
			tabAccessbility.mount();

			const analyticsProps = {
				memberId,
				...(pageType === 'storefront' && {
					storefront_completion_status: vendor?.vendor?.completionScore,
				}),
			};
			analytics?.page(pageType, analyticsProps);

			window?.analytics?.ready(() => {
				settingActions.requestAnonymousId()(dispatch, getState, undefined);
			});

			const initialJotaiState = getJotaiInitialValues(
				window?.__INITIAL_JOTAI_STATE__,
			);

			// Create the Jotai store
			const jotaiStore = createJotaiStore();
			for (const [atom, value] of initialJotaiState) {
				jotaiStore.set(atom, value);
			}
			jotaiStore.set(reduxStoreAtom, store);

			/**
			 * @callback UnloadListenerCallback
			 */

			/** @type {UnloadListenerCallback} */
			const beforeUnloadListener = () => {
				const appliedFilters = jotaiStore.get(appliedFacetsForImpressionsAtom);
				const categoryCodes = jotaiStore.get(selectedCategoryCodesAtom);
				dispatch(
					transmitImpressions({
						transmitMinimumLimit: 1,
						location: state.location,
						appliedFilters,
						categoryCodes,
					}),
				);
			};

			if (typeof window !== 'undefined') {
				window.onbeforeunload = beforeUnloadListener;
			}

			const AppContent = () => {
				useViewportSync(); // Sync viewport state on resize
				return (
					<BrowserRouter>
						<GtmSnippet />
						{renderRoutes(routes, { pageType })}
					</BrowserRouter>
				);
			};

			const App = () => {
				return (
					<ErrorBoundary
						onError={logError}
						fallback={
							<div className="error">
								<div>An Error Occurred</div>
							</div>
						}
					>
						<Provider store={store}>
							<JotaiProvider store={jotaiStore}>
								<HelmetProvider>
									<AnalyticsProvider
										reduxExperiments={window.__INITIAL_STATE__?.experiments}
									>
										<AppContent />
									</AnalyticsProvider>
								</HelmetProvider>
							</JotaiProvider>
						</Provider>
					</ErrorBoundary>
				);
			};

			loadableReady(() => {
				const reporter = createHydrationErrorReporter();
				window.addEventListener('pagehide', reporter.flush);

				hydrateRoot(document.getElementById('app'), <App />, {
					onRecoverableError: reporter.onRecoverableError,
				});
			});
		})
		.catch((error) => {
			logError(error, {
				name: 'ensureAsyncReady failure in client/index.jsx',
				routes,
			});

			throw error;
		});
}

reportWebVitals();
